import { useApi, useWorkspace } from '~/contexts';
import { useField, useIsMounted } from '~/hooks';
import projectTaskCategoryTypes from '~/lookups/project-task-category-types';
import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { colors, weights } from '~/styles';
import { getColorHex } from './taskCategoryColors';
import AutoComplete from './AutoComplete';
import Dropdown from './Dropdown';
import FieldControl from './FieldControl';
import Icon from './Icon';

const Group = styled(Dropdown.Text)`
  font-weight: ${weights.bold};
  font-size: 1rem;
  color: ${colors.grey100};
`;

const Option = styled.span`
  display: flex;
  align-items: center;
`;

const IconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  margin-right: 0.5rem;
  flex-shrink: 0;
`;

function ProjectTaskCategorySelect({ name, value, initialValue, type, useDefault = false, onChange, ...props }) {
  const api = useApi();
  const isMounted = useIsMounted();
  const { workspace } = useWorkspace();
  const [defaultOptions, setDefaultOptions] = useState([]);

  const handleSearch = useCallback(
    async (q) => {
      const { data } = await api.www
        .workspaces(workspace.id)
        .projectTaskCategories()
        .get({ q, isActive: true, type, include: initialValue ? initialValue.id : undefined });

      if (type) return data.map((taskCategory) => ({ type: 'taskCategory', taskCategory, disabled: false }));

      const typeGroups = Object.values(projectTaskCategoryTypes).map(({ id, name }) => ({ key: id, label: name }));

      const options = [];
      for (const group of typeGroups) {
        const items = data.filter((taskCategory) => taskCategory.type === group.key);
        if (items.length === 0) continue;
        options.push({ type: 'group', group, disabled: true });
        items.forEach((taskCategory) => {
          options.push({ type: 'taskCategory', taskCategory, disabled: false });
        });
      }
      return options;
    },
    [workspace.id, initialValue, type, api],
  );

  const refreshDefaultOptions = useCallback(async () => {
    const data = await handleSearch('');
    if (!isMounted.current) return;
    setDefaultOptions(data);
  }, [handleSearch, isMounted]);

  useEffect(() => {
    refreshDefaultOptions();
  }, [refreshDefaultOptions]);

  useEffect(() => {
    if (useDefault && !value && defaultOptions.length > 0) {
      const defaultOption = defaultOptions.find((o) => o.type === 'taskCategory' && o.taskCategory.isDefault);
      if (defaultOption && _.isFunction(onChange)) {
        onChange({ target: { name, value: defaultOption.taskCategory } });
      }
    }
  }, [useDefault, defaultOptions, name, value, onChange]);

  const handleChange = (event) => {
    const changeArgument = { target: { name: event.target.name, value: event.target.value?.taskCategory } };
    onChange(changeArgument);
  };

  const renderOption = (option) => (
    <Option>
      <IconWrapper>
        <Icon icon={option.taskCategory.icon} color={getColorHex(option.taskCategory.color)} />
      </IconWrapper>
      {option.taskCategory.name}
    </Option>
  );

  return (
    <AutoComplete
      name={name}
      onSearch={handleSearch}
      value={value}
      onChange={handleChange}
      displayText={value ? value.name : ''}
      defaultOptions={defaultOptions}
      renderOption={renderOption}
      renderValue={(option) => option.taskCategory.name}
      {...props}>
      {({ options }) => {
        let index = 0;
        return options.length > 0 ? (
          options.map((o) => {
            switch (o.type) {
              case 'group':
                return (
                  <Group key={o.group.key}>
                    <strong>{o.group.label}</strong>
                  </Group>
                );
              case 'taskCategory':
                return (
                  <AutoComplete.Option
                    padding="0.25rem 0 0.25rem 2rem"
                    key={o.taskCategory.id}
                    value={o}
                    index={index++}>
                    {renderOption(o)}
                  </AutoComplete.Option>
                );
              default:
                return null;
            }
          })
        ) : (
          <Dropdown.Text>None</Dropdown.Text>
        );
      }}
    </AutoComplete>
  );
}

function FieldProjectTaskCategorySelect(props) {
  const [field, meta] = useField(props);
  const error = meta.touched && meta.error;

  return (
    <FieldControl error={error}>
      <ProjectTaskCategorySelect {...field} {...props} />
    </FieldControl>
  );
}

export default ProjectTaskCategorySelect;
export { FieldProjectTaskCategorySelect };
